import React, { useState, useEffect, useRef, useCallback } from 'react';
import TublyLogo from './components/TublyLogo';
import { CHROME_STORE_URL } from './constants';
import { 
  DownloadIcon, 
  UserSyncIcon, 
  SparklesIcon, 
  ChevronRightIcon, 
  SummaryIcon, 
  KeyMomentsIcon, 
  AudioIcon, 
  MockChannelItem
} from './components/Icons';

/* =========================================================================
   Topic-aware photos (Unsplash CDN crops, 16:9 @ 1280×720)
   - You can later replace any URL with a local path (/img/photos/*.jpg)
   - Deterministic picker keeps visuals stable between renders
   ========================================================================= */

const PHOTOS = {
  crypto: [
    'https://unsplash.com/photos/VM_6EtTAfDQ/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/H1jdfM5jUxM/download?force=true&w=1280&h=720&fit=crop',
  ],
  psych: [
    'https://unsplash.com/photos/rNSQ-EuBi6o/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/tw-mAZXr6H4/download?force=true&w=1280&h=720&fit=crop',
  ],
  mma: [
    'https://unsplash.com/photos/9RF7fK7ujFs/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/jHUckCgZSGc/download?force=true&w=1280&h=720&fit=crop',
  ],
  english: [
    'https://unsplash.com/photos/c6QLJhezaYs/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/Y8TiLvKnLeg/download?force=true&w=1280&h=720&fit=crop',
  ],
  generic: [
    'https://unsplash.com/photos/mV8vk4Ds3r8/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/MA1Xobltz5o/download?force=true&w=1280&h=720&fit=crop',
  ],
} as const;

const TOPIC_MAP: Array<{ keys: RegExp[]; pool: readonly string[] }> = [
  { keys: [/crypto|bitcoin|ethereum|defi|fomc|markets?|trading/i], pool: PHOTOS.crypto },
  { keys: [/schizo|psych|therapy|neuro|mental|brain|trauma/i],     pool: PHOTOS.psych },
  { keys: [/mma|ufc|fight|boxing|kick|jiu[-\s]?jitsu|wrestling/i],  pool: PHOTOS.mma },
  { keys: [/english|language|vocab|ielts|toefl|learn/i],            pool: PHOTOS.english },
];

function hash32(s: string): number {
  let h = 0x811c9dc5; // FNV-1a base
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return h >>> 0;
}

function pickPhotoByTopic(title: string, channel: string, seed = 0): string {
  const text = `${title} ${channel}`;
  for (const bucket of TOPIC_MAP) {
    if (bucket.keys.some(rx => rx.test(text))) {
      const i = Math.abs(hash32(text + seed)) % bucket.pool.length;
      return bucket.pool[i];
    }
  }
  const g = PHOTOS.generic;
  return g[Math.abs(hash32(text + seed)) % g.length];
}

/* =========================================================================
   Topic-aware channel avatars (Unsplash face crops, 96×96) + local fallback
   - If a channel isn't mapped (or network blocks), we render a data:URL
     gradient with the channel initials (no external fetch needed).
   ========================================================================= */

const AVATARS: Record<string, string[]> = {
  'Psych Talk': [
    'https://images.unsplash.com/photo-1554151228-14d9def656e4?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
  'MMA Breakdown': [
    'https://images.unsplash.com/photo-1598970434795-0c54fe7c0642?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
  'English Lab': [
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
};

function pickAvatarForChannel(channel: string): string | null {
  const pool = AVATARS[channel];
  if (!pool?.length) return null;
  const i = hash32(channel) % pool.length;
  return pool[i];
}

// Gradient initials data URL (always available offline)
function initialsAvatarDataUrl(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('');
  const hue = hash32(name) % 360;
  const bg1 = `hsl(${hue},85%,60%)`;
  const bg2 = `hsl(${(hue + 25) % 360},85%,50%)`;
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 96 96' role='img' aria-label='${name}'>
      <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${bg1}'/><stop offset='100%' stop-color='${bg2}'/>
      </linearGradient></defs>
      <rect width='96' height='96' rx='24' fill='url(#g)'/>
      <text x='50%' y='54%' font-family='ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto' font-size='36' font-weight='700' text-anchor='middle' fill='white'>${initials}</text>
    </svg>`
  );
  return `data:image/svg+xml;charset=utf-8,${svg}`;
}

function getChannelAvatar(channel: string): string {
  return pickAvatarForChannel(channel) ?? initialsAvatarDataUrl(channel);
}

/* =========================================================================
   UI Components
   ========================================================================= */

const MockVideoItem: React.FC<{ title: string; channel: string; thumb?: string }> = ({ title, channel, thumb }) => {
  const src = thumb ?? pickPhotoByTopic(title, channel, 1);
  return (
    <div className="flex flex-col group bg-white rounded-lg shadow-sm overflow-hidden text-left w-full">
      <div className="w-full aspect-video relative bg-gray-200">
        <img src={src} alt={title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-2">
        <h3 className="text-xs font-semibold text-gray-800 line-clamp-2">{title}</h3>
        <p className="text-[10px] text-gray-500 mt-1">{channel}</p>
      </div>
    </div>
  );
};

const MockInsightItem: React.FC<{ title: string; channel: string; thumb?: string; insight: string }> = ({ title, channel, thumb, insight }) => {
  const src = thumb ?? pickPhotoByTopic(title, channel, 2);
  return (
    <div className="flex-shrink-0 w-4/5">
      <div className="p-2 text-left bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
        <div className="w-full">
          <img src={src} alt={title} className="w-full aspect-video object-cover rounded-lg" loading="lazy" />
        </div>
        <div className="flex-1 mt-2 flex flex-col">
          <p className="text-[10px] font-semibold text-red-600 truncate">{channel}</p>
          <h3 className="text-xs font-bold text-gray-800 line-clamp-2 mt-0.5">{title}</h3>
          <p className="text-[10px] text-gray-500 leading-snug line-clamp-3 mt-1">{insight}</p>
        </div>
      </div>
    </div>
  );
};

const MockPopupView: React.FC = () => (
  <div className="w-full h-full bg-gray-50 rounded-2xl shadow-2xl group-hover:shadow-[0_35px_70px_-20px_rgba(0,0,0,0.4)] ring-1 ring-gray-900/10 overflow-hidden flex flex-col transition-shadow duration-1000">
    <header className="flex items-center justify-center p-3 border-b border-gray-200 bg-white flex-shrink-0">
      <div className="flex items-center gap-2">
        <TublyLogo className="w-7 h-auto" />
        <h1 className="text-lg font-bold text-gray-900">tubly</h1>
      </div>
    </header>

    <div className="p-4 space-y-4 overflow-y-auto scrollbar-hide">
      {/* Today's Answers */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">Today's Answers</h2>
        <div className="flex overflow-x-auto space-x-3 -mx-4 px-4 pb-2 scrollbar-hide">
          <MockInsightItem
            title="Schizophrenia Explained in 10 Key Ideas"
            channel="Psych Talk"
            insight="Sums up the ten pillars behind schizophrenia—positive and negative symptoms, the dopamine-glutamate imbalance, and why early intervention with family routines keeps relapses at bay."
          />
          <MockInsightItem
            title="UFC Game Plan: 5 Momentum Swings"
            channel="MMA Breakdown"
            insight="Spells out the five swing tactics: calf kicks to sap pressure fighters, jab exits to own the cage, level-change feints into doubles, clinch breaks with elbows, and a last-round cardio surge that steals the cards."
          />
        </div>
      </div>

      {/* Your Channels */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">Your Channels</h2>
        <div className="flex space-x-3">
          <MockChannelItem name="Psych Talk"   thumb={getChannelAvatar('Psych Talk')}   count={5} isSelected={true} />
          <MockChannelItem name="MMA Breakdown" thumb={getChannelAvatar('MMA Breakdown')} count={3} isSelected={false} />
          <MockChannelItem name="English Lab"   thumb={getChannelAvatar('English Lab')}   count={1} isSelected={false} />
        </div>
      </div>

      {/* New Releases */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">New Releases</h2>
        <div className="grid grid-cols-2 gap-3">
          <MockVideoItem title="Schizophrenia Explained in 10 Key Ideas" channel="Psych Talk" />
          <MockVideoItem title="UFC Game Plan: 5 Momentum Swings" channel="MMA Breakdown" />
        </div>
      </div>
    </div>
  </div>
);

const MockYoutubeView: React.FC = () => {
  const title = `The Most DANGEROUS Week In Crypto [Don't Make This Mistake]`;
  const channel = 'Lab Of Crypto';
  return (
    <div className="w-full h-full bg-[#0f0f0f] text-white font-[Roboto,Arial,sans-serif] flex flex-col overflow-hidden rounded-2xl shadow-2xl group-hover:shadow-[0_35px_70px_-20px_rgba(0,0,0,0.4)] ring-1 ring-gray-900/10 transition-shadow duration-1000">
      {/* Fake Video Player */}
      <div className="w-full h-[210px] bg-black flex items-center justify-center overflow-hidden">
        <img src={pickPhotoByTopic(title, channel, 3)} alt="Video player thumbnail" className="w-full h-full object-cover" loading="lazy" />
      </div>

      {/* Fake Video Info */}
      <div className="p-3">
        <h2 className="text-md font-bold leading-tight line-clamp-2">{title}</h2>
        <div className="flex items-center mt-3">
          <img className="w-9 h-9 rounded-full" src={getChannelAvatar(channel)} alt={`${channel} avatar`} />
          <div className="ml-3">
            <p className="font-semibold text-sm">{channel}</p>
            <p className="text-xs text-gray-400">170K subscribers</p>
          </div>
        </div>
      </div>

      {/* Tubly Summary Block */}
      <div className="mx-3 mt-1 p-3 bg-[#282828] rounded-xl border border-gray-700/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <TublyLogo className="w-6 h-auto" />
            <h3 className="font-bold text-sm">Tubly Summary</h3>
          </div>
          <div className="flex text-xs bg-black/30 p-1 rounded-lg">
            <button className="px-3 py-1 bg-[#f1f1f1] text-black rounded-md font-semibold">Direct Answer</button>
            <button className="px-3 py-1 text-gray-300">Summary</button>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-300 leading-relaxed">
          The host calls FOMC week the "danger zone" because thin liquidity lets whales trigger cascading liquidations. His fix: rotate into stables, trim leverage before the rate decision, and wait for the post-announcement whipsaw to settle before re-entering.
        </p>
      </div>
    </div>
  );
};

/* =========================================================================
   Page Sections
   ========================================================================= */

const Hero: React.FC = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopFlipping = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startFlipping = useCallback(() => {
    stopFlipping();
    intervalRef.current = setInterval(() => {
      setIsFlipped(prev => !prev);
    }, 6000);
  }, [stopFlipping]);

  useEffect(() => {
    startFlipping();
    return () => stopFlipping();
  }, [startFlipping, stopFlipping]);

  const handleFlip = () => {
    setIsFlipped(prev => !prev);
    if (intervalRef.current) startFlipping();
  };

  const transformClass = isFlipped ? '[transform:rotateY(180deg)]' : '';
  const hoverTransformClass = isFlipped
    ? 'group-hover:[transform:scale(1.05)_rotateY(175deg)_rotateX(5deg)]'
    : 'group-hover:[transform:scale(1.05)_rotateY(-5deg)_rotateX(5deg)]';

  return (
    <section className="pt-32 pb-64 bg-white">
      <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div className="text-center md:text-left">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Your YouTube, <span className="text-red-500">Summarized.</span>
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Tubly uses AI and Youtube API to give you direct generated answers of new videos from your subscribed channels. Save time, stay informed, and jump straight to the important parts.
          </p>
          <div className="mt-8">
            <a href={CHROME_STORE_URL} className="bg-red-500 text-white font-bold py-3 px-8 rounded-lg text-lg hover:bg-red-600 transition-transform hover:scale-105 inline-block shadow-lg">
              Add to Chrome — It's Free
            </a>
          </div>
        </div>

        <div
          className="relative flex justify-center h-[550px] [perspective:1200px] group cursor-pointer"
          onMouseEnter={stopFlipping}
          onMouseLeave={startFlipping}
          onClick={handleFlip}
        >
          <div
            className={`relative w-[380px] h-full transition-transform duration-1000 [transform-style:preserve-3d] ${transformClass} ${hoverTransformClass}`}
          >
            {/* Front Side */}
            <div className="absolute w-full h-full [backface-visibility:hidden]">
              <MockPopupView />
            </div>
            {/* Back Side */}
            <div className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
              <MockYoutubeView />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg border border-red-100 hover:border-red-300 transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer">
    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-500">
      {icon}
    </div>
    <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{children}</p>
  </div>
);

const Features: React.FC = () => (
  <section id="features" className="py-32 bg-gray-50">
    <div className="container mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900">Everything you need to stay ahead.</h2>
        <p className="mt-3 text-md text-gray-600">Tubly is packed with powerful features to supercharge your YouTube experience.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FeatureCard icon={<SummaryIcon className="w-6 h-6" />} title="AI Summaries">
          Summaries AI reduces your consumption time and surface the most important insights from every video.
        </FeatureCard>
        <FeatureCard icon={<KeyMomentsIcon className="w-6 h-6" />} title="Key Moments">
          Play by moments and jump straight to the exact scenes that matter the most.
        </FeatureCard>
        <FeatureCard icon={<AudioIcon className="w-6 h-6" />} title="Saved Highlights">
          Save and revisit highlights, chapters, and timestamp clips without replaying full videos.
        </FeatureCard>
        <FeatureCard icon={<DownloadIcon className="w-6 h-6" />} title="Download Transcripts">
          Get full editable transcripts with one click for note-taking, studying, or research.
        </FeatureCard>
      </div>
    </div>
  </section>
);

const StepCard: React.FC<{ icon: React.ReactNode; step: string; title: string; children: React.ReactNode }> = ({ icon, step, title, children }) => (
  <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/50 flex-1 text-center transition-transform duration-300 hover:scale-105 cursor-pointer">
    <div className="flex items-center justify-center h-16 w-16 mx-auto rounded-full bg-red-100 text-red-500 mb-5">
      {icon}
    </div>
    <p className="font-bold text-red-500 mb-2">{step}</p>
    <h3 className="text-xl font-semibold text-gray-900 mb-3">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
  </div>
);

const HowItWorks: React.FC = () => (
  <section id="how-it-works" className="py-52 bg-white">
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl font-bold text-gray-900">Get Started in Seconds</h2>
        <p className="mt-3 text-md text-gray-600">Using Tubly is as easy as 1, 2, 3.</p>
      </div>
      <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-6">
        <StepCard icon={<DownloadIcon className="w-8 h-8" />} step="Step 1" title="Install Extension">
          Add Tubly to your Chrome browser from the official Web Store.
        </StepCard>
        <div className="hidden md:block transform rotate-90 md:rotate-0">
          <ChevronRightIcon className="w-10 h-10 text-gray-300" />
        </div>
        <StepCard icon={<UserSyncIcon className="w-8 h-8" />} step="Step 2" title="Connect Your Account">
          Securely sign in with Google to sync your YouTube subscriptions. No risky extensions. No file downloads.
        </StepCard>
        <div className="hidden md:block transform rotate-90 md:rotate-0">
          <ChevronRightIcon className="w-10 h-10 text-gray-300" />
        </div>
        <StepCard icon={<SparklesIcon className="w-8 h-8" />} step="Step 3" title="Get the Crux">
          See summaries, timestamps, and transcripts instantly — and open the exact moment on YouTube.
        </StepCard>
      </div>
    </div>
  </section>
);

const LandingPage: React.FC = () => {
  return (
    <>
      <main>
        <Hero />
        <Features />
        <HowItWorks />
      </main>
    </>
  );
};

export default LandingPage;
